/* views/Orders.jsx — v5 logic, v6 design */

import React from 'react'
import { humanize, formatMoney, formatDate, formatDateTime, formatScheduled, scheduledFor, ENUM_OPTIONS } from '../utils.jsx'
import { Orders, Contacts, Products, Production, Riders, selectAll } from '../db.jsx'
import { Pill, StatusDot, statusTone, Drawer, Modal, ConfirmModal, Field, EnumSelect, PhoneInput, EmptyState, Spinner, FilterBar, useFilteredRows, useToast } from '../components.jsx'
import { I } from '../icons.jsx'
import { reportError } from '../errors.jsx'

function OrdersView({ goto }) {
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [filters, setFilters] = React.useState({ q: "", status: "", from: "", to: "" });
  const [showNew, setShowNew] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("scheduled");
  const [showCompleted, setShowCompleted] = React.useState(false);

  React.useEffect(() => {
    const ctrl = new AbortController();
    Orders.list(ctrl.signal)
      .then(r => setRows(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setRows([]));
    return () => ctrl.abort();
  }, []);

  const refresh = React.useCallback(() => {
    Orders.list().then(r => {
      const list = Array.isArray(r) ? r : (r.data || []);
      setRows(list);
      // Keep the open drawer in sync with fresh data (so balance_due etc. update)
      setOpen(prev => {
        if (!prev) return prev;
        const fresh = list.find(x => x.id === prev.id);
        return fresh || prev;
      });
    }).catch(() => setRows([]));
  }, []);

  const filterDefs = React.useMemo(() => ([
    { type: "search", key: "q", placeholder: "Search order # or customer…", fields: ["order_number", "contact_name"] },
    { type: "select", key: "status", label: "All statuses", options: ENUM_OPTIONS.order_status },
    { type: "date",   key: "from",   label: "From",  column: "created_at" },
    { type: "date",   key: "to",     label: "To",    column: "created_at" },
  ]), []);
  const filtered = useFilteredRows(rows || [], filterDefs, filters);

  const visibleRows = React.useMemo(() => {
    let list = filtered;
    if (!showCompleted && !filters.status) {
      list = list.filter(r => !["delivered", "cancelled"].includes(r.status));
    }
    if (sortBy === "scheduled") {
      list = [...list].sort((a, b) => {
        const sa = scheduledFor(a)?.date;
        const sb = scheduledFor(b)?.date;
        if (!sa && !sb) return 0;
        if (!sa) return 1;
        if (!sb) return -1;
        return sa < sb ? -1 : sa > sb ? 1 : 0;
      });
    } else {
      list = [...list].sort((a, b) => {
        const ca = a.created_at || "";
        const cb = b.created_at || "";
        return cb < ca ? -1 : cb > ca ? 1 : 0;
      });
    }
    return list;
  }, [filtered, showCompleted, sortBy, filters.status]);

  return (
    <div className="view-enter">
      <div className="filter-bar">
        <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 280px", minWidth: 200 }}>
          <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
          <input placeholder="Search order # or customer…"
                 value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <select className="select" style={{ width: "auto", minWidth: 160 }}
                value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          {ENUM_OPTIONS.order_status.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        <select className="select" style={{ width: "auto", minWidth: 140 }}
                value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="scheduled">Sort: Scheduled</option>
          <option value="created">Sort: Created</option>
        </select>
        <button className={`btn btn-sm ${showCompleted ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setShowCompleted(v => !v)}>
          {showCompleted ? "Hide completed" : "Show completed"}
        </button>
        <span className="spacer"/>
        <button className="btn btn-ghost btn-sm">{I.download} Export</button>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>{I.plus} New order</button>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          {rows === null
            ? <Spinner label="Loading orders…" />
            : visibleRows.length === 0
              ? <EmptyState title="No orders" hint={rows.length ? "No orders match your filters." : "Create your first order."} />
              : <table className="tbl tbl-orders">
                  <thead><tr>
                    <th>Order #</th><th>Customer</th><th>Status</th>
                    <th>Payment</th><th>Delivery</th>
                    <th style={{ textAlign: "right" }}>Total</th><th>Scheduled</th>
                  </tr></thead>
                  <tbody>
                    {visibleRows.map(r => (
                      <tr key={r.id} onClick={() => setOpen(r)} className={open?.id === r.id ? "selected" : ""}>
                        <td className="mono">{r.order_number || r.id?.slice(0, 8)}</td>
                        <td style={{ fontWeight: 600 }}>{r.contact_name || "—"}</td>
                        <td><Pill tone={statusTone(r.status)}><span className="dot"/>{humanize(r.status)}</Pill></td>
                        <td>{r.payment_method ? <Pill tone="muted">{humanize(r.payment_method)}</Pill> : <span className="faint">—</span>}</td>
                        <td className="muted">{r.delivery_method ? humanize(r.delivery_method) : "—"}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(r.total)}</td>
                        <td className="muted">{formatScheduled(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
          }
        </div>
      </div>

      {open && <OrderDrawer order={open} onClose={() => setOpen(null)} onChanged={refresh} />}
      {showNew && <NewOrderDrawer onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
    </div>
  );
}

export function OrderDrawer({ order, onClose, onChanged }) {
  const toast = useToast();
  const [items, setItems] = React.useState([]);
  const [tab, setTab] = React.useState("items");
  const [statusBusy, setStatusBusy] = React.useState(false);
  const [currentStatus, setCurrentStatus] = React.useState(order.status);
  const [pendingStatus, setPendingStatus] = React.useState(null);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState("");

  React.useEffect(() => {
    const ctrl = new AbortController();
    Orders.items(order.id, ctrl.signal)
      .then(r => setItems(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setItems([]));
    return () => ctrl.abort();
  }, [order.id]);

  function changeStatus(newStatus) {
    if (newStatus === currentStatus) return;
    setPendingStatus(newStatus);
  }

  async function confirmStatusChange() {
    setStatusBusy(true);
    const { error } = await Orders.updateStatus(order.id, pendingStatus);
    if (!error) {
      setCurrentStatus(pendingStatus);
      onChanged();
      toast.push({ tone: "ok", title: "Status updated", msg: humanize(pendingStatus) });
    }
    setPendingStatus(null);
    setStatusBusy(false);
  }

  async function confirmCancel() {
    await Orders.cancel(order.id, cancelReason || null);
    toast.push({ tone: "warn", title: "Order cancelled" });
    setCancelOpen(false);
    setCancelReason("");
    onChanged(); onClose();
  }

  return (
    <>
    <Drawer
      title={<><span className="txt-grad">{order.order_number || order.id?.slice(0, 8)}</span><Pill tone={statusTone(currentStatus)}><span className="dot"/>{humanize(currentStatus)}</Pill></>}
      subtitle={`${order.contact_name || ""} · ${formatDateTime(order.created_at)}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-danger" onClick={() => setCancelOpen(true)} disabled={order.status === "cancelled"}>Cancel order</button>
        <span className="spacer" />
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </>}
    >
      <div className="grid c3" style={{ marginBottom: 14 }}>
        <div className="kpi" style={{ padding: "14px 16px" }}>
          <div className="kpi-head"><span>Subtotal</span></div>
          <div className="kpi-value" style={{ fontSize: 20, marginTop: 6 }}>{formatMoney(order.subtotal)}</div>
        </div>
        <div className="kpi" style={{ padding: "14px 16px" }}>
          <div className="kpi-head"><span>Total</span></div>
          <div className="kpi-value" style={{ fontSize: 20, marginTop: 6 }}>{formatMoney(order.total)}</div>
        </div>
        <div className="kpi" style={{ padding: "14px 16px" }}>
          <div className="kpi-head"><span>Balance due</span></div>
          <div className="kpi-value" style={{ fontSize: 20, marginTop: 6, color: (order.balance_due || 0) > 0 ? "var(--warn)" : "var(--ok)" }}>{formatMoney(order.balance_due)}</div>
        </div>
      </div>

      {currentStatus !== "cancelled" && (
        <div style={{ marginBottom: 14 }}>
          <Field label="Change status">
            <select className="select" value={currentStatus} disabled={statusBusy}
                    onChange={e => changeStatus(e.target.value)}>
              {(ENUM_OPTIONS.order_status || []).map(o => (
                <option key={o} value={o}>{humanize(o)}</option>
              ))}
            </select>
          </Field>
        </div>
      )}

      <div className="tabs">
        <button className={tab === "items" ? "active" : ""} onClick={() => setTab("items")}>Items</button>
        <button className={tab === "payments" ? "active" : ""} onClick={() => setTab("payments")}>Payments</button>
        <button className={tab === "details" ? "active" : ""} onClick={() => setTab("details")}>Details</button>
      </div>
      {tab === "items" && <ItemsTab order={order} items={items} setItems={setItems} onChanged={onChanged} />}
      {tab === "payments" && <PaymentsTab order={order} onChanged={onChanged} />}
      {tab === "details" && <DetailsTab order={order} onChanged={onChanged} />}
    </Drawer>

    {pendingStatus && (
      <ConfirmModal
        title="Change status"
        message={`Change to "${humanize(pendingStatus)}"?`}
        confirmLabel="Change"
        onConfirm={confirmStatusChange}
        onClose={() => setPendingStatus(null)}
      />
    )}
    {cancelOpen && (
      <Modal title="Cancel order" onClose={() => { setCancelOpen(false); setCancelReason(""); }}
        footer={<>
          <button className="btn btn-ghost" type="button" onClick={() => { setCancelOpen(false); setCancelReason(""); }}>Back</button>
          <button className="btn btn-danger" type="button" onClick={confirmCancel}>Cancel order</button>
        </>}>
        <Field label="Reason (optional)">
          <input className="input" value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                 placeholder="e.g. Customer request" autoFocus />
        </Field>
      </Modal>
    )}
    </>
  );
}

function ItemsTab({ order, items, setItems, onChanged }) {
  const toast = useToast();
  const [confirmRemoveId, setConfirmRemoveId] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [pid, setPid] = React.useState("");
  const [qty, setQty] = React.useState(1);
  const [price, setPrice] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [editing, setEditing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [flavorSets, setFlavorSets] = React.useState([]);
  const [selectedFlavors, setSelectedFlavors] = React.useState("");
  const [expandedItemId, setExpandedItemId] = React.useState(null);
  const [batchLinkItemId, setBatchLinkItemId] = React.useState(null);
  const [inventoryItems, setInventoryItems] = React.useState([]);

  React.useEffect(() => {
    Products.list().then(r => setProducts((Array.isArray(r) ? r : (r.data || [])).filter(p => p.active !== false)));
    Production.items().then(r => setInventoryItems(Array.isArray(r) ? r : (r.data || [])));
  }, []);

  const reload = () => Orders.items(order.id).then(r => setItems(Array.isArray(r) ? r : (r.data || [])));

  function pickProduct(id) {
    setPid(id);
    setSelectedFlavors("");
    const p = products.find(x => x.id === id);
    if (p && (price === "" || price == null)) setPrice(p.price_retail ?? p.retail_price ?? "");
    if (id) {
      Products.flavorSets(id).then(r => setFlavorSets((Array.isArray(r) ? r : (r.data || [])).filter(s => s.active)));
    } else {
      setFlavorSets([]);
    }
  }

  async function addItem() {
    if (!pid || !qty) return;
    setBusy(true);
    await Orders.addItem({ p_order_id: order.id, p_product_id: pid, p_quantity: Number(qty), p_unit_price: price === "" ? null : Number(price), p_notes: notes || null });

    // If a flavor was selected, find the auto-created batch and patch it
    if (selectedFlavors) {
      try {
        const batches = await selectAll("batches_list", { eq: { order_id: order.id }, order: { column: "created_at", ascending: false } });
        const batch = (Array.isArray(batches) ? batches : (batches.data || []))[0];
        if (batch) {
          const patchRes = await Production.update(batch.id, {
            p_flavors: selectedFlavors,
            p_date_made: batch.date_made,
            p_qty_produced: batch.qty_produced,
            p_unit: batch.unit,
            p_notes: batch.notes || null,
          });
          if (patchRes.error) toast.push({ tone: "warn", title: "Item added — flavor patch failed", msg: "Set flavors manually in the Production view." });
        }
      } catch {
        toast.push({ tone: "warn", title: "Item added — flavor patch failed", msg: "Set flavors manually in the Production view." });
      }
    }

    setPid(""); setQty(1); setPrice(""); setNotes(""); setSelectedFlavors(""); setFlavorSets([]);
    await reload(); onChanged?.();
    toast.push({ tone: "ok", title: "Item added" });
    setBusy(false);
  }

  async function saveEdit() {
    setBusy(true);
    await Orders.updateItem({ p_id: editing.id, p_quantity: Number(editing.quantity), p_unit_price: Number(editing.unit_price), p_notes: editing.notes || null });
    setEditing(null);
    await reload(); onChanged?.();
    toast.push({ tone: "ok", title: "Item updated" });
    setBusy(false);
  }

  async function doRemoveItem(id) {
    await Orders.removeItem(id);
    await reload(); onChanged?.();
    toast.push({ tone: "ok", title: "Item removed" });
  }

  return (
    <div>
      <table className="tbl">
        <thead><tr>
          <th style={{ width: 28 }}></th>
          <th>Item</th>
          <th style={{ textAlign: "right" }}>Qty</th>
          <th style={{ textAlign: "right" }}>Price</th>
          <th style={{ textAlign: "right" }}>Subtotal</th>
          <th>Notes</th>
          <th>Batch</th>
          <th></th>
        </tr></thead>
        <tbody>
          {items.length === 0
            ? <tr><td colSpan="8" className="muted" style={{ textAlign: "center", padding: 20 }}>No items yet — add one below.</td></tr>
            : items.map(i => {
                const isExpanded = expandedItemId === i.id;
                if (editing?.id === i.id) return (
                  <tr key={i.id}>
                    <td></td>
                    <td>{i.product_name}</td>
                    <td><input className="input" type="number" min="1" value={editing.quantity} style={{ width: 70 }} onChange={e => setEditing({ ...editing, quantity: e.target.value })}/></td>
                    <td><input className="input" type="number" step="0.01" value={editing.unit_price} style={{ width: 90 }} onChange={e => setEditing({ ...editing, unit_price: e.target.value })}/></td>
                    <td className="amt" style={{ textAlign: "right" }}>{formatMoney(Number(editing.quantity || 0) * Number(editing.unit_price || 0))}</td>
                    <td><input className="input" value={editing.notes || ""} onChange={e => setEditing({ ...editing, notes: e.target.value })}/></td>
                    <td></td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={busy}>{I.check}</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>{I.x}</button>
                      </div>
                    </td>
                  </tr>
                );
                return (
                  <React.Fragment key={i.id}>
                    <tr style={{ cursor: "default" }}>
                      <td style={{ textAlign: "center" }}>
                        <button className="btn btn-ghost btn-sm" style={{ padding: "2px 4px", fontSize: 11 }}
                                onClick={() => setExpandedItemId(isExpanded ? null : i.id)}
                                title={isExpanded ? "Collapse" : "Show batch ingredients"}>
                          {isExpanded ? "▼" : "▶"}
                        </button>
                      </td>
                      <td>{i.product_name}</td>
                      <td className="mono" style={{ textAlign: "right" }}>{i.quantity}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{formatMoney(i.unit_price)}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{formatMoney(i.subtotal)}</td>
                      <td className="muted">{i.notes || <span className="faint">—</span>}</td>
                      <td>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 6px" }}
                                onClick={() => setBatchLinkItemId(i.id)}
                                title="Link or unlink batch">
                          {I.link ?? "⚙"} Batch
                        </button>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditing({ id: i.id, quantity: i.quantity, unit_price: i.unit_price, notes: i.notes })}>{I.edit}</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemoveId(i.id)}>{I.trash}</button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${i.id}-exp`}>
                        <td colSpan="8" style={{ padding: 0 }}>
                          <BatchIngredientsPanel itemId={i.id} orderId={order.id} inventoryItems={inventoryItems} onChanged={reload} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
          }
        </tbody>
      </table>
      {confirmRemoveId && (
        <ConfirmModal title="Remove item" message="Remove this item from the order?"
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveItem(confirmRemoveId)}
          onClose={() => setConfirmRemoveId(null)} />
      )}
      {batchLinkItemId && (
        <BatchLinkModal
          itemId={batchLinkItemId}
          orderId={order.id}
          onClose={() => setBatchLinkItemId(null)}
          onChanged={() => { setExpandedItemId(null); reload(); onChanged?.(); }}
        />
      )}
      <div style={{ marginTop: 12, padding: 12, background: "var(--surface-2)", borderRadius: 10, border: "1px dashed var(--border-strong)" }}>
        <div className="form-row-collapse" style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 1fr 1.2fr auto", gap: 8 }}>
          <select className="select" value={pid} onChange={e => pickProduct(e.target.value)}>
            <option value="">— Select product —</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.price_retail ? ` · ${formatMoney(p.price_retail)}` : ""}</option>)}
          </select>
          <input className="input" type="number" min="1" placeholder="Qty" value={qty} onChange={e => setQty(e.target.value)}/>
          <input className="input" type="number" step="0.01" placeholder="Unit price" value={price} onChange={e => setPrice(e.target.value)}/>
          <input className="input" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}/>
          <button className="btn btn-primary" onClick={addItem} disabled={!pid || busy}>{I.plus} Add</button>
        </div>
        {flavorSets.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <select className="select" value={selectedFlavors} onChange={e => setSelectedFlavors(e.target.value)}>
              <option value="">— Select flavor set (optional) —</option>
              {flavorSets.map(s => <option key={s.id} value={s.flavors}>{s.set_name} · {s.flavors}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

function BatchIngredientsPanel({ itemId, orderId, inventoryItems, onChanged }) {
  const toast = useToast();
  const [batch, setBatch] = React.useState(undefined);
  const [ings, setIngs] = React.useState([]);
  const [editingIng, setEditingIng] = React.useState(null);
  const [confirmRemoveIngId, setConfirmRemoveIngId] = React.useState(null);
  const [addForm, setAddForm] = React.useState({ item_id: '', qty_used: '', unit: '', notes: '' });
  const [addBusy, setAddBusy] = React.useState(false);

  React.useEffect(() => {
    Production.batchByOrderItem(itemId).then(({ data }) => {
      setBatch(data || null);
      if (data) {
        Production.ingredients(data.id).then(r => setIngs(Array.isArray(r) ? r : (r.data || [])));
      }
    });
  }, [itemId]);

  const reloadIngs = () => {
    if (batch) Production.ingredients(batch.id).then(r => setIngs(Array.isArray(r) ? r : (r.data || [])));
  };

  if (batch === undefined) return <div style={{ padding: '12px 16px' }}><Spinner label="Loading batch…" /></div>;
  if (!batch) return (
    <div style={{ padding: '10px 16px', color: 'var(--text-faint)', fontSize: 13, fontStyle: 'italic' }}>
      No batch linked to this item — use the Batch button to link one.
    </div>
  );

  async function doAddIng() {
    if (!addForm.item_id || !addForm.qty_used) return;
    setAddBusy(true);
    const res = await Production.addIngredient(batch.id, addForm.item_id, Number(addForm.qty_used), addForm.unit, addForm.notes || null, null, null);
    if (!res?.error) {
      setAddForm({ item_id: '', qty_used: '', unit: '', notes: '' });
      reloadIngs(); onChanged?.();
      toast.push({ tone: 'ok', title: 'Ingredient added' });
    }
    setAddBusy(false);
  }

  async function doSaveIng() {
    await Production.updateIngredient(editingIng.id, Number(editingIng.qty_used), editingIng.unit, editingIng.notes);
    setEditingIng(null); reloadIngs(); onChanged?.();
    toast.push({ tone: 'ok', title: 'Ingredient updated' });
  }

  async function doRemoveIng(id) {
    await Production.removeIngredient(id);
    reloadIngs(); onChanged?.();
    toast.push({ tone: 'ok', title: 'Ingredient removed' });
  }

  return (
    <div style={{ padding: '8px 16px 12px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
        Batch ingredients · {batch.product_name_snapshot} · {formatDate(batch.date_made)}
      </div>
      {ings.length > 0 && (
        <table className="tbl" style={{ marginBottom: 8, fontSize: 13 }}>
          <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th><th>Notes</th><th></th></tr></thead>
          <tbody>
            {ings.map(ing => editingIng?.id === ing.id ? (
              <tr key={ing.id}>
                <td>{ing.item_name}</td>
                <td><input className="input" type="number" step="any" value={editingIng.qty_used} style={{ width: 70 }} onChange={e => setEditingIng(p => ({ ...p, qty_used: e.target.value }))} /></td>
                <td><input className="input" value={editingIng.unit} style={{ width: 80 }} onChange={e => setEditingIng(p => ({ ...p, unit: e.target.value }))} /></td>
                <td><input className="input" value={editingIng.notes || ''} onChange={e => setEditingIng(p => ({ ...p, notes: e.target.value }))} /></td>
                <td><div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-primary btn-sm" onClick={doSaveIng}>{I.check}</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingIng(null)}>{I.x}</button>
                </div></td>
              </tr>
            ) : (
              <tr key={ing.id} style={{ cursor: 'default' }}>
                <td>{ing.item_name}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{ing.qty_used}</td>
                <td className="muted">{ing.unit}</td>
                <td className="muted">{ing.notes || <span className="faint">—</span>}</td>
                <td><div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingIng({ id: ing.id, qty_used: String(ing.qty_used), unit: ing.unit || '', notes: ing.notes || '' })}>{I.edit}</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemoveIngId(ing.id)}>{I.trash}</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {ings.length === 0 && <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 8 }}>No ingredients yet — add one below.</div>}
      <div className="form-row-collapse" style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 0.8fr 1.5fr auto', gap: 6 }}>
        <select className="select" value={addForm.item_id} onChange={e => {
          const it = inventoryItems.find(x => x.id === e.target.value);
          setAddForm(p => ({ ...p, item_id: e.target.value, unit: it?.default_unit || p.unit }));
        }}>
          <option value="">— Select item —</option>
          {inventoryItems.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
        </select>
        <input className="input" type="number" step="any" placeholder="Qty" value={addForm.qty_used} onChange={e => setAddForm(p => ({ ...p, qty_used: e.target.value }))} />
        <input className="input" placeholder="Unit" value={addForm.unit} onChange={e => setAddForm(p => ({ ...p, unit: e.target.value }))} />
        <input className="input" placeholder="Notes (optional)" value={addForm.notes} onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))} />
        <button className="btn btn-primary btn-sm" onClick={doAddIng} disabled={!addForm.item_id || !addForm.qty_used || addBusy}>{I.plus}</button>
      </div>
      {confirmRemoveIngId && (
        <ConfirmModal title="Remove ingredient" message="Remove this ingredient from the batch?"
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveIng(confirmRemoveIngId)}
          onClose={() => setConfirmRemoveIngId(null)} />
      )}
    </div>
  );
}

function BatchLinkModal({ itemId, orderId, onClose, onChanged }) {
  const toast = useToast();
  const [batch, setBatch] = React.useState(undefined);
  const [available, setAvailable] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    Production.batchByOrderItem(itemId).then(({ data }) => {
      setBatch(data || null);
      if (!data) {
        Production.pendingBatches().then(r => setAvailable(Array.isArray(r) ? r : (r.data || [])));
      }
    });
  }, [itemId]);

  async function doUnlink() {
    setBusy(true);
    await Production.unlinkFromOrder(batch.id);
    toast.push({ tone: 'ok', title: 'Batch unlinked' });
    onChanged(); onClose();
  }

  async function doLink(batchId) {
    setBusy(true);
    await Production.linkToOrderItem(batchId, orderId, itemId);
    toast.push({ tone: 'ok', title: 'Batch linked' });
    onChanged(); onClose();
  }

  const filtered = (available || []).filter(b =>
    !search ||
    (b.product_name_snapshot || '').toLowerCase().includes(search.toLowerCase()) ||
    formatDate(b.date_made).includes(search)
  );

  if (batch === undefined) return (
    <Modal title="Batch" onClose={onClose}>
      <Spinner label="Loading…" />
    </Modal>
  );

  if (batch) return (
    <Modal title="Linked batch" onClose={onClose}
      footer={<>
        <button className="btn btn-danger" onClick={doUnlink} disabled={busy}>Unlink batch</button>
        <span className="spacer" />
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </>}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700 }}>{batch.product_name_snapshot}</div>
        <div className="muted" style={{ marginTop: 4 }}>{formatDate(batch.date_made)} · {batch.qty_produced} {batch.unit}</div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-faint)' }}>
        Unlinking keeps the batch in Production but removes its link to this order item.
      </div>
    </Modal>
  );

  return (
    <Modal title="Link batch to item" onClose={onClose}>
      <input className="input" placeholder="Search by product or date…" value={search}
             onChange={e => setSearch(e.target.value)} style={{ marginBottom: 10 }} autoFocus />
      {available === null
        ? <Spinner label="Loading batches…" />
        : filtered.length === 0
          ? <div className="muted" style={{ textAlign: 'center', padding: 16 }}>No pending unlinked batches found.</div>
          : <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {filtered.map(b => (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{b.product_name_snapshot}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{formatDate(b.date_made)} · {b.qty_produced} {b.unit}</div>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => doLink(b.id)} disabled={busy}>Link</button>
                </div>
              ))}
            </div>
      }
    </Modal>
  );
}

function PaymentsTab({ order, onChanged }) {
  const toast = useToast();
  const [pays, setPays] = React.useState([]);
  const [amt, setAmt] = React.useState("");
  const [method, setMethod] = React.useState("cash");
  const [ref, setRef] = React.useState("");
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null);

  const reload = React.useCallback(() => {
    Orders.payments(order.id).then(r => setPays(Array.isArray(r) ? r : (r.data || [])));
  }, [order.id]);

  React.useEffect(() => {
    const ctrl = new AbortController();
    Orders.payments(order.id, ctrl.signal)
      .then(r => setPays(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setPays([]));
    return () => ctrl.abort();
  }, [order.id]);

  async function add() {
    const amount = Number(amt);
    if (!amt || amount <= 0) {
      reportError({ code: "WYF-VAL-001", where: "Payments / add", message: "Amount must be greater than zero." });
      return;
    }
    await Orders.addPayment({
      p_order_id: order.id, p_amount: amount, p_payment_method: method,
      p_payment_reference: ref || null, p_payment_proof_url: null, p_paid_at: null, p_notes: null,
    });
    setAmt(""); setRef("");
    reload();
    toast.push({ tone: "ok", title: "Payment recorded" });
    onChanged();
  }

  async function doDeletePayment(id) {
    await Orders.removePayment(id);
    await reload();
    onChanged();
    toast.push({ tone: "ok", title: "Payment deleted" });
  }

  const paid = pays.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = (order.total || 0) - paid;

  return (
    <div>
      <div className="grid c3" style={{ marginBottom: 14 }}>
        <div className="kpi" style={{ padding: "12px 14px" }}>
          <div className="kpi-head"><span>Order total</span></div>
          <div className="kpi-value" style={{ fontSize: 18, marginTop: 6 }}>{formatMoney(order.total)}</div>
        </div>
        <div className="kpi" style={{ padding: "12px 14px" }}>
          <div className="kpi-head"><span>Paid</span></div>
          <div className="kpi-value" style={{ fontSize: 18, marginTop: 6, color: "var(--ok)" }}>{formatMoney(paid)}</div>
        </div>
        <div className="kpi" style={{ padding: "12px 14px" }}>
          <div className="kpi-head"><span>Balance</span></div>
          <div className="kpi-value" style={{ fontSize: 18, marginTop: 6, color: balance > 0 ? "var(--warn)" : "var(--ok)" }}>{formatMoney(balance)}</div>
        </div>
      </div>

      <table className="tbl" style={{ marginBottom: 12 }}>
        <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th style={{ textAlign: "right" }}>Amount</th><th></th></tr></thead>
        <tbody>
          {pays.length === 0
            ? <tr><td colSpan="5" className="muted" style={{ textAlign: "center", padding: 16 }}>No payments yet.</td></tr>
            : pays.map(p => (
              <tr key={p.id} style={{ cursor: "default" }}>
                <td className="muted">{formatDateTime(p.paid_at)}</td>
                <td><Pill tone="muted">{humanize(p.payment_method)}</Pill></td>
                <td className="mono faint" style={{ fontSize: 12 }}>{p.payment_reference || "—"}</td>
                <td className="amt" style={{ textAlign: "right" }}>{formatMoney(p.amount)}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteId(p.id)} title="Delete payment">{I.trash}</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <div className="form-row-collapse" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, padding: 12, background: "var(--surface-2)", borderRadius: 10, border: "1px dashed var(--border-strong)" }}>
        <input className="input" type="number" min="0.01" step="0.01" placeholder={`Amount (balance: ${formatMoney(balance)})`} value={amt} onChange={e => setAmt(e.target.value)} />
        <EnumSelect value={method} onChange={setMethod} options={ENUM_OPTIONS.order_payment_method} />
        <input className="input" placeholder="Reference (optional)" value={ref} onChange={e => setRef(e.target.value)} />
        <button className="btn btn-primary" onClick={add} disabled={!amt}>{I.plus} Add</button>
      </div>
      {confirmDeleteId && (
        <ConfirmModal title="Delete payment" message="Remove this payment record? This cannot be undone."
          confirmLabel="Delete" danger
          onConfirm={() => doDeletePayment(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)} />
      )}
    </div>
  );
}

function DetailsTab({ order, onChanged }) {
  const toast = useToast();
  const [riders, setRiders] = React.useState([]);
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const buildForm = React.useCallback(() => ({
    delivery_method: order.delivery_method || '',
    delivery_date: order.delivery_date || '',
    delivery_address: order.delivery_address || '',
    pickup_location: order.pickup_location || '',
    meetup_location: order.meetup_location || '',
    meetup_datetime: order.meetup_datetime ? order.meetup_datetime.slice(0, 16) : '',
    rider_id: order.rider_id || '',
    or_number: order.or_number || '',
    notes: order.notes || '',
  }), [order]);

  const [form, setForm] = React.useState(buildForm);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  React.useEffect(() => {
    Riders.list().then(r => setRiders((Array.isArray(r) ? r : (r.data || [])).filter(x => x.active !== false)));
  }, []);

  function startEdit() { setForm(buildForm()); setEditing(true); }

  async function save() {
    setBusy(true);
    const { error } = await Orders.updateDetails(order.id, form);
    setBusy(false);
    if (!error) {
      toast.push({ tone: 'ok', title: 'Details updated' });
      onChanged();
      setEditing(false);
    }
  }

  const dm = editing ? form.delivery_method : (order.delivery_method || '');
  const isDelivery = dm === 'delivery';
  const isPickup = dm === 'pick_up';
  const isMeetup = dm === 'meet_up';

  if (!editing) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={startEdit}>{I.edit} Edit</button>
        </div>
        <Field label="Delivery method"><div>{humanize(order.delivery_method) || "—"}</div></Field>
        {isDelivery && <Field label="Delivery date"><div>{formatDate(order.delivery_date)}</div></Field>}
        {isDelivery && <Field label="Delivery address"><div>{order.delivery_address || <span className="faint">—</span>}</div></Field>}
        {isPickup && <Field label="Pickup location"><div>{order.pickup_location || <span className="faint">—</span>}</div></Field>}
        {isMeetup && <Field label="Meetup location"><div>{order.meetup_location || <span className="faint">—</span>}</div></Field>}
        {isMeetup && <Field label="Meetup date & time"><div>{order.meetup_datetime ? formatDateTime(order.meetup_datetime) : <span className="faint">—</span>}</div></Field>}
        <Field label="OR number"><div>{order.or_number || <span className="faint">—</span>}</div></Field>
        <Field label="Notes"><div className="muted" style={{ whiteSpace: "pre-wrap" }}>{order.notes || <span className="faint">No notes.</span>}</div></Field>
      </div>
    );
  }

  return (
    <div>
      <Field label="Delivery method">
        <EnumSelect value={form.delivery_method} onChange={v => set('delivery_method', v)} options={ENUM_OPTIONS.delivery_method} />
      </Field>
      {isDelivery && <Field label="Delivery date"><input className="input" type="date" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} /></Field>}
      {isDelivery && <Field label="Delivery address"><textarea className="textarea" value={form.delivery_address} onChange={e => set('delivery_address', e.target.value)} /></Field>}
      {isDelivery && form.delivery_date && (
        <Field label="Rider">
          <select className="select" value={form.rider_id} onChange={e => set('rider_id', e.target.value)}>
            <option value="">— Unassigned —</option>
            {riders.map(r => <option key={r.id} value={r.id}>{r.name}{r.service_type ? ` · ${humanize(r.service_type)}` : ''}</option>)}
          </select>
        </Field>
      )}
      {isPickup && (
        <Field label="Pickup location">
          <select className="select" value={form.pickup_location} onChange={e => set('pickup_location', e.target.value)}>
            <option value="">— Select location —</option>
            <option value="Home">Home</option>
            <option value="Mabini Drop-off">Mabini Drop-off</option>
          </select>
        </Field>
      )}
      {isMeetup && <Field label="Meetup location"><input className="input" value={form.meetup_location} onChange={e => set('meetup_location', e.target.value)} /></Field>}
      {isMeetup && <Field label="Meetup date & time"><input className="input" type="datetime-local" value={form.meetup_datetime} onChange={e => set('meetup_datetime', e.target.value)} /></Field>}
      <Field label="OR number"><input className="input" value={form.or_number} onChange={e => set('or_number', e.target.value)} /></Field>
      <Field label="Notes"><textarea className="textarea" value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : <>{I.check} Save</>}</button>
      </div>
    </div>
  );
}

// Day-of-week mapping: JS getDay() 0=Sun, 1=Mon, … 6=Sat
const DOW_NAMES = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

function NewOrderDrawer({ onClose, onSaved }) {
  const toast = useToast();
  const [contacts, setContacts] = React.useState([]);
  const [riders, setRiders] = React.useState([]);
  const [availableRiderIds, setAvailableRiderIds] = React.useState(null); // null = no date filter
  const [f, setF] = React.useState({
    contact: "", status: "pending", delivery_method: "pick_up",
    delivery_date: "", meetup_datetime: "", meetup_location: "",
    delivery_address: "", delivery_fee: "", discount: "", tip: "", notes: "",
    rider_id: "", pickup_location: "",
  });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    Contacts.list().then(r => setContacts(Array.isArray(r) ? r : (r.data || [])));
    Riders.list().then(r => setRiders((Array.isArray(r) ? r : (r.data || [])).filter(x => x.active !== false)));
  }, []);

  // When delivery date changes, load which riders are available that day
  React.useEffect(() => {
    if (!f.delivery_date) { setAvailableRiderIds(null); return; }
    const date = new Date(f.delivery_date + "T12:00:00");
    if (isNaN(date.getTime())) { setAvailableRiderIds(null); return; }
    const dayName = DOW_NAMES[date.getDay()];
    if (!dayName) { setAvailableRiderIds(null); return; }
    selectAll("rider_availability", { eq: { day_of_week: dayName } }).then(r => {
      const ids = new Set((r.data || []).map(x => x.rider_id));
      setAvailableRiderIds(ids);
    });
  }, [f.delivery_date]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isDelivery = f.delivery_method === "delivery";

  // Riders filtered by delivery date availability
  const ridersForDate = availableRiderIds === null
    ? riders
    : riders.filter(r => r.is_always_available || availableRiderIds.has(r.id));

  async function save() {
    if (!f.contact) return;
    setBusy(true);
    const res = await Orders.create({
      p_contact_id: f.contact, p_status: f.status,
      p_delivery_method: f.delivery_method, p_delivery_date: f.delivery_date || null,
      p_delivery_address: f.delivery_address || null, p_delivery_fee: Number(f.delivery_fee) || 0,
      p_discount: Number(f.discount) || 0, p_tip: Number(f.tip) || 0, p_notes: f.notes || null,
      p_rider_id: isDelivery && f.rider_id ? f.rider_id : null,
      p_meetup_datetime: f.meetup_datetime || null,
      p_meetup_location: f.meetup_location || null,
      p_pickup_location: f.delivery_method === "pick_up" ? (f.pickup_location || null) : null,
      p_promo_code_id: f.promo_code_id || null,
    });
    setBusy(false);
    if (!res.error) {
      toast.push({ tone: "ok", title: "Order created" });
      onSaved();
    }
  }

  return (
    <Drawer title="New order" onClose={onClose}
      footer={<>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !f.contact}>
          {busy ? "Saving…" : <>{I.check} Create</>}
        </button>
      </>}>
      <Field label="Customer" required>
        <select className="select" value={f.contact} onChange={e => set("contact", e.target.value)}>
          <option value="">— Select customer —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.business_name ? ` · ${c.business_name}` : ""}</option>)}
        </select>
      </Field>
      <Field label="Status"><EnumSelect value={f.status} onChange={v => set("status", v)} options={ENUM_OPTIONS.order_status} /></Field>
      <Field label="Delivery method"><EnumSelect value={f.delivery_method} onChange={v => set("delivery_method", v)} options={ENUM_OPTIONS.delivery_method} /></Field>
      <Field label="Delivery date"><input className="input" type="date" value={f.delivery_date} onChange={e => set("delivery_date", e.target.value)} /></Field>
      {isDelivery && (
        <Field label="Rider" help={f.delivery_date ? `Showing riders available on ${humanize(DOW_NAMES[new Date(f.delivery_date + "T12:00:00").getDay()])} or always-available.` : "Set a delivery date to filter by availability."}>
          <select className="select" value={f.rider_id} onChange={e => set("rider_id", e.target.value)}>
            <option value="">— Unassigned —</option>
            {ridersForDate.map(r => <option key={r.id} value={r.id}>{r.name}{r.service_type ? ` · ${humanize(r.service_type)}` : ""}{r.is_always_available ? " ✓" : ""}</option>)}
          </select>
          {f.delivery_date && ridersForDate.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--warn)", marginTop: 4 }}>No riders scheduled for this day. Check rider availability settings.</div>
          )}
        </Field>
      )}

      {f.delivery_method === "pick_up" && (
        <Field label="Pickup location">
          <select className="select" value={f.pickup_location || ""} onChange={e => set("pickup_location", e.target.value)}>
            <option value="">— Select location —</option>
            <option value="Home">Home</option>
            <option value="Mabini Drop-off">Mabini Drop-off</option>
          </select>
        </Field>
      )}
      {f.delivery_method === "meet_up" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Meetup date & time"><input className="input" type="datetime-local" value={f.meetup_datetime} onChange={e => set("meetup_datetime", e.target.value)} /></Field>
          <Field label="Meetup location"><input className="input" value={f.meetup_location} onChange={e => set("meetup_location", e.target.value)} placeholder="e.g. SM Baguio"/></Field>
        </div>
      )}
      {isDelivery && <Field label="Delivery address"><textarea className="textarea" value={f.delivery_address} onChange={e => set("delivery_address", e.target.value)} /></Field>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Field label="Delivery fee"><input className="input" type="number" value={f.delivery_fee} onChange={e => set("delivery_fee", e.target.value)} /></Field>
        <Field label="Discount"><input className="input" type="number" value={f.discount} onChange={e => set("discount", e.target.value)} /></Field>
        <Field label="Tip"><input className="input" type="number" value={f.tip} onChange={e => set("tip", e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)} /></Field>
    </Drawer>
  );
}

export { OrdersView };
